import React, { useState, useMemo } from 'react';
import { DUNGEON_BIOMES } from '../../data/world-map/dungeonBiomes';
import { DUNGEON_CONFIG, LOOT_TABLES, ENEMY_TEMPLATES, DUNGEON_MOB_POOLS } from '../../data/dungeon';

const DungeonEntryModal = ({ biomeId, playerLevel, currentEra, onEnter, onCancel }) => {
  if (!biomeId) return null;

  const biome = DUNGEON_BIOMES[biomeId];
  if (!biome || !biome.isDungeonBiome) return null;

  // Get the base tier number from the biome's dungeonTier (e.g., 'tier1' -> 1)
  const baseTierNum = parseInt(biome.dungeonTier.replace('tier', ''));

  // Get the dungeon type from the biome ID (e.g., 'ancientRuins')
  const dungeonType = biomeId;

  // Get all dungeons for this specific dungeon type that the player can access
  const availableDungeons = useMemo(() => {
    return DUNGEON_CONFIG.TIERS.filter(d =>
      d.dungeonType === dungeonType &&
      (currentEra || 1) >= d.minEra &&
      playerLevel >= d.minLevel
    ).sort((a, b) => b.minLevel - a.minLevel); // Highest level first
  }, [dungeonType, playerLevel, currentEra]);

  // Get all dungeons for this specific dungeon type (for showing locked ones)
  const allTierDungeons = useMemo(() => {
    return DUNGEON_CONFIG.TIERS.filter(d => d.dungeonType === dungeonType)
      .sort((a, b) => a.minLevel - b.minLevel); // Lowest level first for display
  }, [dungeonType]);

  // Default to highest available dungeon
  const [selectedDungeonId, setSelectedDungeonId] = useState(
    availableDungeons[0]?.id || null
  );

  const selectedDungeon = DUNGEON_CONFIG.TIERS.find(d => d.id === selectedDungeonId);

  // If no dungeon is available (player level too low), show the first dungeon as locked
  if (!selectedDungeon && allTierDungeons.length > 0) {
    const firstDungeon = allTierDungeons[0];
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal-content dungeon-entry-modal" onClick={(e) => e.stopPropagation()}>
          <div className="dem-header">
            <div className="dem-title">
              <span className="dem-icon">{biome.icon}</span>
              <div className="dem-title-text">
                <h2>{biome.name}</h2>
                <p className="dem-subtitle">Level {firstDungeon.minLevel} Required</p>
              </div>
            </div>
            <button className="close-btn" onClick={onCancel}>×</button>
          </div>
          <div className="dem-body">
            <div className="dem-desc" style={{ textAlign: 'center', color: '#c06060' }}>
              You must reach level {firstDungeon.minLevel} to enter this dungeon.
              <br />
              <span style={{ color: '#9a8e7a' }}>Current Level: {playerLevel}</span>
            </div>
            <div className="dem-desc" style={{ textAlign: 'center', marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(128, 184, 88, 0.15)', borderRadius: '4px', borderLeft: '3px solid var(--success)' }}>
              <span style={{ color: 'var(--success)' }}>💡 Tip:</span>{' '}
              <span style={{ color: 'var(--text-secondary)' }}>
                Fight mobs in <strong style={{ color: 'var(--accent-gold)' }}>Encounter tiles</strong> (⛺🐺🕷️) to earn XP and level up!
              </span>
            </div>
          </div>
          <div className="dem-footer">
            <button className="dem-btn dem-btn-cancel" onClick={onCancel}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedDungeon) return null;

  // Get loot table info
  const lootTable = LOOT_TABLES[selectedDungeon.lootTable] || LOOT_TABLES.common;
  const rarityWeights = lootTable.rarityWeights;

  // Determine difficulty color based on selected dungeon
  const levelDiff = playerLevel - selectedDungeon.minLevel;
  let difficultyColor = '#f59e0b'; // Amber (even match)
  let difficultyText = 'Recommended';

  if (levelDiff >= 10) {
    difficultyColor = '#22c55e'; // Green (easy)
    difficultyText = 'Easy';
  } else if (levelDiff >= 5) {
    difficultyColor = '#a3e635'; // Light green (manageable)
    difficultyText = 'Manageable';
  } else if (levelDiff <= -5) {
    difficultyColor = '#dc2626'; // Red (very hard)
    difficultyText = 'Very Hard';
  } else if (levelDiff <= -2) {
    difficultyColor = '#f97316'; // Orange (hard)
    difficultyText = 'Hard';
  }

  const handleEnter = () => {
    onEnter(selectedDungeonId);
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content dungeon-entry-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="dem-header">
          <div className="dem-title">
            <span className="dem-icon">{biome.icon}</span>
            <div className="dem-title-text">
              <h2>{biome.name}</h2>
              <p className="dem-subtitle">Tier {baseTierNum} Dungeon</p>
            </div>
          </div>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {/* Compact Grid Body */}
        <div className="dem-body">
          {/* Description - Full Width */}
          <div className="dem-desc">{biome.description}</div>

          {/* Dungeon Selection Grid */}
          <div className="dem-dungeon-select">
            <div className="dem-section-header">Select Dungeon (Lv {playerLevel})</div>
            <div className="dem-dungeon-grid">
              {allTierDungeons.map(dungeon => {
                const isAvailable = playerLevel >= dungeon.minLevel && (currentEra || 1) >= dungeon.minEra;
                const isSelected = selectedDungeonId === dungeon.id;

                return (
                  <button
                    key={dungeon.id}
                    className={`dem-dungeon-btn ${isSelected ? 'selected' : ''} ${!isAvailable ? 'locked' : ''}`}
                    onClick={() => isAvailable && setSelectedDungeonId(dungeon.id)}
                    disabled={!isAvailable}
                    title={isAvailable ? dungeon.name : `Requires Level ${dungeon.minLevel}`}
                  >
                    <span className="dem-dungeon-level">Lv{dungeon.minLevel}</span>
                    <span className="dem-dungeon-name">{dungeon.name}</span>
                    {!isAvailable && <span className="dem-dungeon-lock">🔒</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stats Grid - 4 columns */}
          <div className="dem-stats-grid">
            <div className="dem-stat">
              <div className="dem-stat-label">Dungeon</div>
              <div className="dem-stat-value">{selectedDungeon.name}</div>
            </div>
            <div className="dem-stat">
              <div className="dem-stat-label">Enemy Level</div>
              <div className="dem-stat-value" style={{ color: difficultyColor }}>{selectedDungeon.baseEnemyLevel}</div>
            </div>
            <div className="dem-stat">
              <div className="dem-stat-label">Your Level</div>
              <div className="dem-stat-value">{playerLevel}</div>
            </div>
            <div className="dem-stat">
              <div className="dem-stat-label">Difficulty</div>
              <div className="dem-stat-value" style={{ color: difficultyColor }}>{difficultyText}</div>
            </div>
          </div>

          {/* Enemies & Loot - 2 column grid */}
          <div className="dem-details-grid">
            {/* Enemies - Left (use dungeon-specific mob pool) */}
            <div className="dem-section">
              <div className="dem-section-header">Enemies ({(DUNGEON_MOB_POOLS[selectedDungeonId] || biome.dungeonMobPool).length})</div>
              <div className="dem-mob-grid">
                {(DUNGEON_MOB_POOLS[selectedDungeonId] || biome.dungeonMobPool).map((mobId, idx) => {
                  const mob = ENEMY_TEMPLATES[mobId];
                  return (
                    <div key={idx} className="dem-mob" title={mob?.name || mobId}>
                      <span className="dem-mob-icon">{mob?.icon || '👹'}</span>
                      <span className="dem-mob-name">{mob?.name || mobId}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Loot - Right */}
            <div className="dem-section">
              <div className="dem-section-header">Loot: {selectedDungeon.lootTable.charAt(0).toUpperCase() + selectedDungeon.lootTable.slice(1)}</div>
              <div className="dem-loot-grid">
                {Object.entries(rarityWeights).map(([rarity, weight]) => {
                  if (weight === 0) return null;

                  let rarityColor = '#9a8e7a'; // Gray for common
                  if (rarity === 'uncommon') rarityColor = '#80b858';
                  if (rarity === 'rare') rarityColor = '#6080a8';
                  if (rarity === 'epic') rarityColor = '#806890';
                  if (rarity === 'legendary') rarityColor = '#dab050';
                  if (rarity === 'mythic') rarityColor = '#ff6b9d';

                  return (
                    <div key={rarity} className="dem-loot-row">
                      <span className="dem-loot-name" style={{ color: rarityColor }}>
                        {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                      </span>
                      <div className="dem-loot-bar-wrap">
                        <div className="dem-loot-bar" style={{ width: `${weight}%`, backgroundColor: rarityColor }} />
                      </div>
                      <span className="dem-loot-pct">{weight}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Reward Banner */}
          <div className="dem-reward">
            <span className="dem-reward-label">Completion Reward</span>
            <span className="dem-reward-value">+{selectedDungeon.expReward} EXP</span>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="dem-footer">
          <button className="dem-btn dem-btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="dem-btn dem-btn-enter" onClick={handleEnter}>⚔️ Enter {selectedDungeon.name}</button>
        </div>
      </div>
    </div>
  );
};

export default DungeonEntryModal;
